import React from 'react';
import { Award, Star, Sparkles } from 'lucide-react';

const Awards = () => {
  // Every entry links to the publisher's own page. Only list recognition we
  // can source: anything unverifiable is an ASIC misleading-conduct risk and
  // costs us trust with the LLMs and search engines that cite this page.
  // Last verified 2026-10-07.
  const majorAwards = [
    {
      icon: Sparkles,
      title: '5-Star Mortgage Innovators',
      organization: 'Australian Broker',
      year: '2025',
      color: 'from-green-400 to-green-600',
      description: 'Named in Australian Broker\'s 5-Star Mortgage Innovators list (fewer than 10 employees category).',
      source: 'https://www.brokernews.com.au/best-in-mortgage/best-mortgage-innovations-in-australia--5star-mortgage-innovators-287005.aspx',
    },
  ];

  const finalists = [
    {
      title: 'Bankwest New Brokerage of the Year',
      year: '2024',
      event: 'Australian Mortgage Awards (Excellence Awardee / Finalist)',
      source: 'https://www.brokernews.com.au/news/breaking-news/revealed-australian-mortgage-awards-2024-excellence-awardees-285357.aspx',
    },
    {
      title: 'Innovator of the Year – Brokerage',
      year: '2024',
      event: 'Broker Daily Business Awards',
      source: 'https://www.brokerdaily.au/mortgage-business-awards/winners/2024-winners-and-finalists',
    },
    {
      title: 'New Office of the Year',
      year: '2024',
      event: 'Australian Broking Awards',
      source: 'https://www.theadviser.com.au/australian-broking-awards/winners/2024-winners-and-finalists',
    },
    {
      title: 'Industry Thought Leader of the Year (MYL team member)',
      year: '2025',
      event: 'Australian Broking Awards',
      source: 'https://www.theadviser.com.au/australian-broking-awards/winners/2025-winners-and-finalists',
    },
  ];

  return (
    <section id="awards" className="relative py-20 bg-gradient-to-br from-gray-50 to-white overflow-hidden" data-testid="awards-section">
      {/* Subtle Lattice Pattern Background */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <img 
          src="/images/myl-pattern-lime-green.png"
          alt=""
          role="presentation"
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fadeInUp">
          <div className="inline-flex items-center justify-center space-x-2 bg-[#99DF63]/10 px-6 py-2 rounded-full mb-6">
            <Award className="w-5 h-5 text-[#00594C]" />
            <span className="text-[#00594C] font-semibold">Industry Recognition</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#00594C] mb-4" data-testid="awards-title">
            Awards &amp; Recognition
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Independent industry recognition, each linked to the publisher&apos;s own announcement
          </p>
        </div>

        {/* Major Awards Grid */}
        <div className="grid md:grid-cols-1 max-w-2xl mx-auto gap-8 mb-16">
          {majorAwards.map((award, index) => {
            const Icon = award.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 card-hover"
                data-testid={`major-award-${index}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${award.color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-[#99DF63] bg-[#99DF63]/10 px-3 py-1 rounded-full">
                        {award.year}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-[#00594C] mb-2">
                      {award.title}
                    </h3>
                    {award.subtitle && (
                      <p className="text-md font-semibold text-gray-700 mb-2">
                        {award.subtitle}
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mb-3">
                      {award.organization}
                    </p>
                    <p className="text-sm text-gray-500 italic mb-3">
                      {award.description}
                    </p>
                    <a href={award.source} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#00594C] underline hover:text-[#99DF63] transition-colors">
                      View source
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Finalists */}
        <div className="bg-gradient-to-r from-[#00594C] to-[#003d35] rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-6 text-center" data-testid="finalists-title">
            Finalist Recognition
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {finalists.map((finalist, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
                data-testid={`finalist-${index}`}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <Star className="w-6 h-6 text-[#99DF63]" />
                  <span className="text-sm font-semibold text-[#99DF63]">Finalist · {finalist.year}</span>
                </div>
                <h4 className="font-bold mb-2">{finalist.title}</h4>
                <p className="text-sm text-gray-200 mb-3">{finalist.event}</p>
                <a href={finalist.source} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[#99DF63] underline hover:text-white transition-colors">
                  View source
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Awards;